import { supabase } from '@/lib/supabase'
import type { CallKind } from '@/types/database'

/**
 * Create the durable call row. Everything after this -- offer, answer, ICE --
 * rides Realtime broadcast on `call:<id>` and never touches the database.
 *
 * The partial unique index `calls_one_live_per_conversation` means a second
 * simultaneous attempt loses the race rather than starting a rival call, so we
 * join the existing one instead of erroring.
 */
export async function startCall(
  conversationId: string,
  initiatorId: string,
  kind: CallKind = 'video',
): Promise<string> {
  const { data, error } = await supabase
    .from('calls')
    .insert({ conversation_id: conversationId, initiator_id: initiatorId, kind })
    .select('id')
    .single()

  if (error) {
    if (error.code === '23505') {
      const { data: live } = await supabase
        .from('calls')
        .select('id')
        .eq('conversation_id', conversationId)
        .in('status', ['ringing', 'active'])
        .single()
      if (live) return live.id
    }
    throw error
  }
  return data.id
}

export async function declineCall(callId: string) {
  await supabase
    .from('calls')
    .update({ status: 'declined', ended_at: new Date().toISOString() })
    .eq('id', callId)
}

/** Ring for 45 seconds, then give up and log it as missed. */
export const RING_TIMEOUT_MS = 45_000

export async function markMissed(callId: string) {
  await supabase
    .from('calls')
    .update({ status: 'missed', ended_at: new Date().toISOString() })
    .eq('id', callId)
    .eq('status', 'ringing')
}
